import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { User, Loader2, LogIn, ShieldCheck } from "lucide-react";
import { AdminCard, AdminShell, PasswordInput, adminInputCls, adminLabelCls, formatApiError } from "@/components/admin-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { identifier, password }, { withCredentials: true });
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err, "Invalid email/username or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
        data-testid="admin-login-page"
      >
        <AdminCard title="Admin Portal" subtitle="Secure administrator access">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="admin-identifier-input" className={adminLabelCls}>Email or Username</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="admin-identifier-input"
                  data-testid="admin-identifier-input"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Email or username"
                  autoComplete="username"
                  className={`${adminInputCls} pl-10`}
                />
              </div>
            </div>
            <PasswordInput label="Password" testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-gold disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {loading ? "Signing in…" : "Login"}
            </button>
            <div className="flex items-center justify-between pt-1 text-sm">
              <Link to="/admin/forgot-password" data-testid="link-forgot-password" className="font-medium text-muted transition-colors hover:text-gold">
                Forgot Password?
              </Link>
              <Link to="/admin/forgot-username" data-testid="link-forgot-username" className="font-medium text-muted transition-colors hover:text-gold">
                Forgot Username?
              </Link>
            </div>
          </form>
          <p className="mt-8 flex items-center justify-center gap-2 border-t border-line pt-5 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Authorized personnel only · All activity is protected
          </p>
        </AdminCard>
        <p className="mt-6 text-center text-sm text-muted">
          <Link to="/" data-testid="admin-back-home" className="transition-colors hover:text-navy">← Back to website</Link>
        </p>
      </motion.div>
    </AdminShell>
  );
}
